import AppNavBar from "@/layout/app-bar";
import Sidebar from "@/layout/sidebar";
import { CustomTypography, ThemePalettes } from "@/services/theme-palettes";
import Components from "./styles";
import {
  setLeadOriginTypesToState,
  setTimezonesToState,
} from "@/store/slices/general/generalSlice";
import { createTheme, ThemeProvider } from "@mui/material";
import Box from "@mui/material/Box";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import SessionExpirePrompt from "@/components/session-expire-prompt";
import { globalLogout } from "@/services/generic-logout-method";
import {
  getLoggedinUser,
  selectSessionExpired,
  setUpdatedProfileToState,
  setUserCompaniesCountToState,
} from "@/store/slices/auth/authSlice";
import {
  getLeadSources,
  getOptionsForCreateLead,
  setDomainEmailsToState,
  setLeadOptionsToState,
  setMembersToState,
  setRepresentativesNameToState,
  setLeadSourcesToState,
  getTagColors,
  setCrmMembersToState,
  setRequestTypesToState,
  setCrmCalendarsToState,
} from "@/store/slices/general/dropdownOptionsSlice";
import dropdownOptionsQueries from "./global/dropdownOptionsQueries";
import FeatureService from "@/services/feature-service";
import dropdownEnums, {
  FeatureKeys,
  enum_salutations,
} from "@/services/dropdown-enums";

import { getLeadHashList } from "./settings/components/details/content/automation-templates/sms-templates/helper";
import {
  setAppointmentLeadSearchFields,
  setAppointmentSearchFields,
  setCustomCodesToState,
  setEmailSearchFields,
  setLeadsSearchFields,
  setLeadUpdateFields,
  setSMSSearchFields,
  setTaskSearchFields,
} from "@/store/slices/lead/crmLeadSlice";
import { Get_Custom_Codes } from "./leads/graphql-queries/sms-template-queries";
import UseGql from "@/services/graph-ql/useGql";
import {
  fetchPaginatedData,
  generateOptions,
  makeObjectFromArray,
} from "@/services/generic.methods";
import {
  Get_Appointment_Lead_Search_Fields,
  Get_Appointment_Search_Fields,
  Get_Email_Search_Fields,
  Get_Lead_Info,
  Get_Lead_Search_Fields,
  Get_Lead_Update_Fields,
  Get_SMS_Search_Fields,
  Get_Task_Search_Fields,
  Get_Task_Timers,
} from "./leads/graphql-queries";
import {
  setTriggerCategoriesListToState,
  setTriggersListToState,
} from "@/store/slices/workflow/workflowSlice";
import {
  Get_Company_Business_Category,
  Get_WF_Triggers,
  Get_WF_Triggers_Category,
  Get_WF_Request_Types,
} from "./automation/graphql-queries";
import TourWrapper from "@/components/tour-guide";
import { Get_Domain_Emails } from "./leads/graphql-queries/email-integration";
import {
  Get_Lead_Origin_Types,
  Get_Representatives_Name,
} from "@/services/graph-ql/queries";
import useCRMTheme from "@/hooks/useCRMTheme";
import useFrontdoorTheme from "@/hooks/useFrontdoorTheme";
import FullPageLoader from "@/components/full-page-loader";
import useDesktopNotification from "@/hooks/useDesktopNotification";

import useManageSMSPermission from "./settings/components/details/content/sms-settings/useManagePermission";
import { useCRMDataClient } from "@/services/data-client/DataClientContext";
import { ToastTemplate } from "@/services/variables";
import { toast } from "react-toastify";
import { setTimerRunningInfoToState } from "@/store/slices/tasks/tasksSlice";
import moment from "moment";
import FloatingTimerWidget from "@/components/floating-timer-widget";
import { getAllRequests } from "@/store/slices/requests";
import Task_signalr from "@/services/signal-R/task-signalR-constants";
import OnInitHub from "@/services/signal-R/on-receive-hook";
import {
  Get_Appointment_Calendars,
  Get_Appointment_Members,
} from "./leads/graphql-queries/appointment-scheduling";
import useAuthProvider from "./settings/hooks/useAuthProvider";
const {
  onReceiveMethodNames: { onSaveTaskTimer },
} = Task_signalr;

export default function CRMLayout({ children }) {
  const [showSessionModal, setShowSessionModal] = useState(false);
  const user = useSelector(getLoggedinUser);
  const sessionExpired = useSelector(selectSessionExpired);
  const [loading, setLoading] = useState(true);
  const { themeInfo } = useCRMTheme();

  // Use the new Frontdoor theme hook for REST API-based theme management
  const { themesLoaded, loading: themeLoading } = useFrontdoorTheme();

  const dispatch = useDispatch();
  const requests = useSelector(getAllRequests);
  const { clearStateByMethods } = OnInitHub();
  const { client: dataClient, companyKey } = useCRMDataClient();
  const leadSources = useSelector(getLeadSources);
  const tagColors = useSelector(getTagColors);

  const isCRM = useMemo(() => {
    const features = FeatureService.resolveFeatures(user);
    return FeatureService.hasFeature(features, FeatureKeys.CRM);
  }, [user]);

  const { executeQuery } = UseGql(Get_Custom_Codes, {
    skip: 0,
    take: 30,
  });
  const reportsGQL = UseGql(
    Get_Representatives_Name,
    { variables: {} },
    "report"
  );
  const getLeadFields = UseGql(Get_Lead_Search_Fields);
  const getTaskFields = UseGql(Get_Task_Search_Fields);
  const getLeadUpdateFields = UseGql(Get_Lead_Update_Fields);
  const getSMSFields = UseGql(Get_SMS_Search_Fields);
  const getEmailFields = UseGql(Get_Email_Search_Fields);
  const getAppointmentFields = UseGql(Get_Appointment_Search_Fields);
  const getAppointmentLeadFields = UseGql(Get_Appointment_Lead_Search_Fields);
  const getTriggersCategory = UseGql(Get_WF_Triggers_Category);
  const getRequestTypes = UseGql(Get_WF_Request_Types);
  const getTriggers = UseGql(Get_WF_Triggers);
  const getCompanyBC = UseGql(Get_Company_Business_Category);
  const fetchDomainEmails = UseGql(Get_Domain_Emails);
  const getLeadOriginTypes = UseGql(Get_Lead_Origin_Types);
  const getLead = UseGql(Get_Lead_Info);
  const getMembersGql = UseGql(Get_Appointment_Members);
  const getCalendarsGql = UseGql(Get_Appointment_Calendars);
  const { getSMSPermission } = useManageSMSPermission();
  const { requestPermission } = useDesktopNotification();
  const theme = createTheme({
    palette: ThemePalettes[themeInfo?.mode],
    typography: CustomTypography,
    components: {
      MuiInputBase: { styleOverrides: { root: { fontSize: "0.875rem" } } },
      MuiInputLabel: { styleOverrides: { root: { fontSize: "0.875rem" } } },
      ...Components,
    },
  });
  const TaskTimer = UseGql(Get_Task_Timers);

  const fetchRunningTaskTimers = () => {
    try {
      let payload = {
        where: {
          and: [
            // { leadId: { eq: Number(data?.leadId) } },
            // { taskId: { eq: Number(data?.id) } },
            { status: { eq: "START" } },
            { createdBy: { eq: user?.Email } },
          ],
        },
      };
      TaskTimer.executeQuery(payload).then(async (response) => {
        const { data: res } = response;
        if (res?.taskTimer && res?.taskTimer?.items) {
          const { items } = res.taskTimer;
          console.log(items, "timer items");
          if (items.length) {
            let timer = items[0];
            let leadResponse = await getLead.executeQuery({
              id: timer?.leadId,
            });
            if (
              leadResponse?.data &&
              leadResponse?.data?.leadDetails &&
              leadResponse?.data?.leadDetails?.length
            ) {
              let leadData = leadResponse?.data?.leadDetails[0];
              console.log(leadData, "lead details");
              dispatch(
                setTimerRunningInfoToState({
                  ...timer,
                  startTime: moment(timer?.startTime),
                  leadData: leadData,
                })
              );
            }
          }
        }
      });
    } catch (error) {
      toast.error(ToastTemplate.error(error || error.message));
    }
  };

  useEffect(() => {
    if (requests[onSaveTaskTimer]) {
      console.log("crm layout");

      let response = requests[onSaveTaskTimer];
      console.log(response, "delete");
      if (!response.isSuccess) {
        toast.success(response.errors);
      } else {
        toast.success("Log Added Successfully.");
        dispatch(setTimerRunningInfoToState(null));
      }
      clearStateByMethods([onSaveTaskTimer]);
    }
  }, [requests[onSaveTaskTimer]]);

  useEffect(() => {
    // Inject CSS variables into the :root
    const root = document.documentElement;
    if (themeInfo && theme?.palette?.custom) {
      Object.entries(theme?.palette?.custom).forEach(([key, value]) => {
        root.style.setProperty(`--palette-${key}`, value);
      });
    }
  }, [themeInfo]);

  // Theme is now loaded via useFrontdoorTheme hook
  // This replaces the legacy GraphQL-based user settings approach
  // The hook handles:
  // - GET /api/themes (fetch all available themes)
  // - GET /api/themes/user/current (fetch user's saved theme)
  // - PUT /api/themes/user/{themeId} (save user's theme preference)

  // fetch custom Codes
  const fetchCustomCodes = () => {
    executeQuery().then((res) => {
      let { data } = res;
      if (data?.customCode?.items) {
        dispatch(
          setCustomCodesToState(getLeadHashList(data?.customCode?.items))
        );
      }
    });
  };
  // fetch lead search fields
  const fetchLeadSearchFields = () => {
    let filterKeys = ["LeadOriginTypeId", "State", "Country"];
    getLeadFields.executeQuery().then((res) => {
      let { data } = res;

      if (data?.leadSearchFields?.items) {
        let fields = data?.leadSearchFields?.items?.filter(
          (v) => !filterKeys.includes(v.key)
        );

        dispatch(setLeadsSearchFields(fields));
      }
    });
  };
  const fetchTaskSearchFields = () => {
    // let filterKeys = ["LeadOriginTypeId", "State", "Country"];
    getTaskFields.executeQuery().then((res) => {
      let { data } = res;
      if (data?.taskSearchFields?.items) {
        let fields = data?.taskSearchFields?.items;
        dispatch(setTaskSearchFields(fields));
      }
    });
  };
  const fetchRepresentativesName = () => {
    // let filterKeys = ["LeadOriginTypeId", "State", "Country"];
    reportsGQL.executeQuery().then((res) => {
      let { data } = res;
      if (data?.voiceCallsAgentNames) {
        let names = data?.voiceCallsAgentNames;
        dispatch(setRepresentativesNameToState(names));
      }
    });
  };

  // fetch lead update fields for updating from workflow or any other area
  const fetchLeadUpdateFields = () => {
    getLeadUpdateFields.executeQuery().then((res) => {
      let { data } = res;

      if (data?.leadUpdateFields?.items) {
        let fields = data?.leadUpdateFields?.items;
        dispatch(setLeadUpdateFields(fields));
      }
    });
  };

  // fetch sms search fields
  const fetchSMSSearchFields = () => {
    getSMSFields.executeQuery().then((res) => {
      let { data } = res;
      if (data?.smsSearchFields?.items) {
        dispatch(setSMSSearchFields(data?.smsSearchFields?.items));
      }
    });
  };
  // fetch email search fields
  const fetchEmailSearchFields = () => {
    getEmailFields.executeQuery().then((res) => {
      let { data } = res;
      if (data?.emailSearchFields?.items) {
        dispatch(setEmailSearchFields(data?.emailSearchFields?.items));
      }
    });
  };

  // fetch appointment search fields
  const fetchAppointmentSearchFields = () => {
    getAppointmentFields.executeQuery().then((res) => {
      let { data } = res;
      if (data?.appointmentSearchFields?.items) {
        dispatch(
          setAppointmentSearchFields(data?.appointmentSearchFields?.items)
        );
      }
    });
  };

  // fetch appointment lead search fields
  const fetchAppointmentLeadSearchFields = () => {
    getAppointmentLeadFields.executeQuery().then((res) => {
      let { data } = res;
      if (data?.appointmentLeadSearchFields?.items) {
        dispatch(
          setAppointmentLeadSearchFields(
            data?.appointmentLeadSearchFields?.items
          )
        );
      }
    });
  };

  // fetch Categories
  const fetchCategories = () => {
    getTriggersCategory.executeQuery().then((res) => {
      let { data } = res;
      dispatch(
        setTriggerCategoriesListToState(
          data?.workFlowTriggerCategories?.items || []
        )
      );
    });
  };

  // fetch Request Types
  const fetchRequestTypes = () => {
    getRequestTypes.executeQuery().then((res) => {
      let { data } = res;
      dispatch(setRequestTypesToState(data?.requestType?.items || []));
    });
  };

  // fetch company Category
  const fetchCompanyCategory = (companyKey) => {
    getCompanyBC.executeQuery({ companyKey }).then((res) => {
      let { data } = res;
      if (data?.companyBusinessCategory?.items) {
        let category = {
          businessCategory: data?.companyBusinessCategory?.items[0] || {},
        };
        dispatch(setUpdatedProfileToState(category || {}));
      }
    });
  };
  // fetch Triggers
  const fetchTriggers = () => {
    getTriggers.executeQuery().then((res) => {
      let { data } = res;
      dispatch(setTriggersListToState(data?.workFlowTriggers?.items));
    });
  };

  // fetch timezones
  const fetchTimeZones = () => {
    if (dataClient?.lookups) {
      dataClient.lookups
        .getTimezones()
        .then((res) => {
          if (res && res?.timeZones) {
            let result = res?.timeZones;
            let timezonesOptions = generateOptions(result, "value", "key");
            timezonesOptions = timezonesOptions?.map((item) => ({
              ...item,
              key: null,
            }));
            dispatch(setTimezonesToState(timezonesOptions));
          }
        })
        .catch((error) => {
          console.error("Error fetching timezones:", error);
        });
    }
  };

  // get user total companies count for show/hide switch company option
  const getCompanies = () => {
    let userEmail = user?.Email;
    if (dataClient?.lookups) {
      return dataClient.lookups
        .getUserCompanies(userEmail)
        .then((res) => {
          if (res && res?.userCompanies) {
            let count = res?.userCompanies.length;
            dispatch(setUserCompaniesCountToState(count));
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  // get domains emails to show in dropdowns in several areas
  const getDomainEmails = () => {
    fetchDomainEmails
      .executeQuery()
      .then((res) => {
        let { data } = res;
        if (data?.domainEmail && data?.domainEmail.items) {
          let allDomainEmails = data?.domainEmail?.items;
          let options = generateOptions(
            allDomainEmails,
            "emailAddress",
            "emailAddress"
          );
          options = options.map((opt) => {
            opt.groupByName = "Email Sending from Domains";
            return opt;
          });
          dispatch(setDomainEmailsToState(options));
        }
      })
      .catch((err) => console.log("onError:", err));
  };

  // get appoinment related members
  const fetchMembers = async () => {
    if (!companyKey) return;
    try {
      const data = await dataClient.appointments.getMembers({ companyKey });
      console.log(data, "data======@@@@");
      if (data?.members?.items) {
        dispatch(setMembersToState(data.members.items));
      }
    } catch (err) {
      console.error("[CRMLayout] Error fetching members:", err);
    }
  };

  const fetchCRMMembers = async () => {
    await fetchPaginatedData({
      gqlQuery: getMembersGql,
      dataKey: "members",
      // setData: setMembers,
      dispatch: (items) => dispatch(setCrmMembersToState(items)),
      // setFetchingDone: setMembersFetchingDone,
    });
  };

  const fetchCRMCalendars = async () => {
    await fetchPaginatedData({
      gqlQuery: getCalendarsGql,
      dataKey: "calendars",
      dispatch: (items) => dispatch(setCrmCalendarsToState(items)),
    });
  };

  useEffect(() => {
    let onStorageChange = (event) => {
      if (event.key == "Profile" && event.newValue == null) {
        setShowSessionModal(true);
      }
    };
    // add event listener for storage change
    window.addEventListener("storage", onStorageChange);

    // remove event listener for storage change
    return () => {
      window.removeEventListener("storage", onStorageChange);
    };
  }, []);

  const logoutHandler = () => {
    // send to login page
    let shouldRedirect = true;
    globalLogout(shouldRedirect);
    setShowSessionModal(false);
  };

  const getOptions = async () => {
    const param = {
      companyId: user.CompanyID,
      searchText: "",
    };
    await dispatch(
      getOptionsForCreateLead(
        { request: dropdownOptionsQueries(param, isCRM) },
        false,
        dataClient
      )
    );
    setLoading(false);
  };

  useEffect(() => {
    if (sessionExpired) {
      setShowSessionModal(true);
    }
  }, [sessionExpired]);

  useEffect(() => {
    if (user?.CompanyID) {
      getOptions();
      getCompanies();
    }
  }, [user?.CompanyID]);

  useEffect(() => {
    if (isCRM) {
      fetchCustomCodes();
      fetchEmailSearchFields();
      fetchSMSSearchFields();
      fetchLeadSearchFields();
      fetchTaskSearchFields();
      fetchLeadUpdateFields();
      fetchCategories();
      fetchTriggers();
      getDomainEmails();
      getSMSPermission();
      fetchLeadOriginTypes();
      fetchRepresentativesName();
      fetchRequestTypes();
    }
    fetchTimeZones();
    fetchAppointmentSearchFields();
    fetchAppointmentLeadSearchFields(); // lead fields related to appointment
    // Theme is loaded via useFrontdoorTheme hook
    requestPermission();
    dispatch(
      setLeadOptionsToState({
        Salutation: generateOptions(enum_salutations, "name", "value"),
        initiatedBy: generateOptions(
          dropdownEnums.enum_InitiatedByForGrid,
          "label",
          "id"
        ),
        LeadTypeId: generateOptions(
          dropdownEnums.enum_LeadType,
          "name",
          "value"
        ),
        SourceType: generateOptions(
          dropdownEnums.enum_Source_Type,
          "name",
          "value"
        ),
        status: generateOptions(
          dropdownEnums.enum_importLeadStatus,
          "name",
          "value"
        ),
        source: generateOptions(
          dropdownEnums.enum_importLeadSource,
          "name",
          "value"
        ),
      })
    );
    fetchRunningTaskTimers();
  }, []);

  useEffect(() => {
    if (companyKey) {
      fetchMembers();
      // } else {
      if (isCRM) {
        fetchCRMMembers();
        fetchCRMCalendars();
      }
    }
  }, [companyKey]);

  const fetchLeadOriginTypes = () => {
    getLeadOriginTypes.executeQuery().then((res) => {
      let { data } = res;
      dispatch(
        setLeadOriginTypesToState(
          makeObjectFromArray(data?.leadOriginType?.items, "value", "id")
        )
      );
    });
  };

  useEffect(() => {
    // added separatley due to change in profile state. if we call this on above useEffect, it was runnig multiple times,
    if (user?.CompanyKey) {
      fetchCompanyCategory(user?.CompanyKey);
    }
  }, [user?.CompanyKey]);

  const handleCloseModal = (e, reason) => {
    console.log("reason", reason);
    if (reason === "backdropClick") {
      return false;
    } else {
      setShowSessionModal(false);
    }
  };

  const memoizedLoading = useMemo(() => {
    return loading ? (
      <FullPageLoader message="Please wait while we are loading..." />
    ) : null;
  }, [loading]);

  return (
    <ThemeProvider theme={theme}>
      {/* Fixed Sidebar */}
      {!loading && <Sidebar />}

      {/* Fixed Topbar (offset by sidebar width) */}
      {!loading && <AppNavBar />}

      {/* Main content area */}
      <Box
        className="main-content"
        sx={{
          ml: loading ? 0 : "78px",
          pt: loading ? 0 : "66px",
          backgroundColor: "var(--palette-header_bg, #24183d)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            backgroundColor: "var(--palette-background)",
            borderTopLeftRadius: loading ? 0 : "30px",
            overflow: "hidden",
            minHeight: "calc(100dvh - 140px)",
            pl: "6px",
            pr: "4px",
            display: "flex",
            flexDirection: "column",
            color: "#000",
            // p: "5px"
          }}
        >
          {memoizedLoading}
          {!loading && children}
        </Box>
      </Box>

      <TourWrapper />
      <SessionExpirePrompt
        open={showSessionModal}
        message={"Your session has expired, please login again."}
        onClose={handleCloseModal}
        saveCallback={logoutHandler}
      />
      {!loading && <FloatingTimerWidget />}
    </ThemeProvider>
  );
}
